import React from "react";
import { useQuery } from "react-query";
import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip } from "recharts";
import { communication } from "../../constants/apis/communication";
import "./CurvedAreaCard.css";

// Custom Glassmorphism Tooltip for Spline Area Chart
const CustomAreaTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div className="custom-tooltip-premium" style={{ backgroundColor: "rgba(255, 255, 255, 0.95)", border: "1px solid #e2e8f0", padding: "12px", borderRadius: "8px", boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)" }}>
        <p className="label" style={{ fontWeight: 600, color: "#111827", marginBottom: "8px" }}>{label}</p>
        <div className="tooltip-item d-flex align-items-center gap-2 mb-2">
          <span className="tooltip-indicator" style={{ backgroundColor: "#3a57e8", width: "8px", height: "8px", borderRadius: "50%", display: "inline-block" }}></span>
          <span className="name" style={{ color: "#374151", fontSize: "13px" }}>Overall Production:</span>
          <span className="value font-weight-bold" style={{ color: "#111827", fontSize: "14px" }}>{payload[0].payload.overall || 0}</span>
        </div>
        <div style={{ borderTop: "1px solid #e5e7eb", paddingTop: "8px" }}>
          {Object.entries(payload[0].payload).map(([k, v]) => {
            if (k === "date" || k === "overall") return null;
            return (
              <div key={k} className="d-flex justify-content-between align-items-center" style={{ fontSize: "12px", color: "#6b7280", marginTop: "4px" }}>
                <span>{k}</span>
                <span style={{ fontWeight: 600, color: "#374151" }}>{v}</span>
              </div>
            );
          })}
        </div>
      </div>
    );
  }
  return null;
};

export default function CurvedAreaCard() {
  const { data: rawData, isLoading: loading } = useQuery(
    ["overallProduction"],
    () => communication.getOverallProduction({}),
    {
      refetchOnWindowFocus: false,
    }
  );

  const data = rawData?.data?.data || [];

  return (
    <div className="card-premium p-4 d-flex flex-column justify-content-between relative" style={{ minHeight: "360px" }}>
      <div className="d-flex align-items-center justify-content-between mb-4">
        <h4 className="chart-card-title">Overall Production (Last 30 Days)</h4>
        <div className="d-flex align-items-center gap-3">
          <div className="d-flex align-items-center gap-2">
            <span className="legend-dot" style={{ backgroundColor: "#3a57e8" }}></span>
            <span className="legend-dot" style={{ backgroundColor: "#ff958d" }}></span>
            <span className="legend-dot" style={{ backgroundColor: "#aaff75" }}></span>
          </div>
          <div className="legend-outline-box"></div>
        </div>
      </div>

      {/* Spline Area Chart */}
      <div className="chart-render-container flex-1 relative">
        {/* Custom Tooltip marker pill "0.00" at peak */}
        <div className="absolute-peak-tooltip">
          <div className="peak-tooltip-pill">0.00</div>
          <div className="peak-vertical-line"></div>
          <div className="peak-reference-dot"></div>
        </div>

        <ResponsiveContainer width="100%" height="100%">
          {loading ? (
            <div className="d-flex align-items-center justify-content-center h-100">
              <span>Loading...</span>
            </div>
          ) : data.length === 0 ? (
            <div className="d-flex align-items-center justify-content-center h-100">
              <span style={{ color: "#6b7280" }}>No production data available</span>
            </div>
          ) : (
            <AreaChart data={data} margin={{ top: 20, right: 10, left: -25, bottom: 0 }}>
              <defs>
                <linearGradient id="purpleAreaGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#3a57e8" stopOpacity={0.35} />
                  <stop offset="95%" stopColor="#3a57e8" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid stroke="#e2e8f0" strokeDasharray="3 3" vertical={false} />
              <XAxis 
                dataKey="date" 
                tickLine={false} 
                axisLine={false} 
                tick={{ fill: "#666", fontSize: 11, fontWeight: 600 }} 
                dy={10} 
                tickFormatter={(val) => {
                  if (!val) return "";
                  const d = new Date(val);
                  return `${d.getDate()} ${d.toLocaleString('default', { month: 'short' })}`;
                }}
              />
              <YAxis 
                allowDecimals={false}
                tickLine={false} 
                axisLine={false} 
                tick={{ fill: "#666", fontSize: 12, fontWeight: 600 }} 
                dx={-5} 
              />
              <Tooltip content={<CustomAreaTooltip />} />
              <Area
                type="monotone"
                dataKey="overall"
                stroke="#3a57e8"
                strokeWidth={2}
                fillOpacity={1}
                fill="url(#purpleAreaGrad)"
                dot={{ fill: "#3a57e8", r: 4, strokeWidth: 2, stroke: "#fff" }}
                activeDot={{ r: 6, strokeWidth: 0, fill: "#3a57e8" }}
              />
            </AreaChart>
          )}
        </ResponsiveContainer>
      </div>
    </div>
  );
}
